import apiClient from '@/shared/api/client';
import { ApiResponse } from '@/shared/api/types';

export interface Integration {
  id: string;
  type: string;
  status: 'CONNECTED' | 'DISCONNECTED' | 'ERROR' | 'PENDING';
  lastSyncedAt?: string;
  errorMessage?: string;
}

export interface BaseIntegrationStatus {
  connected: boolean;
  status?: 'connected' | 'disconnected' | 'error' | 'expired';
  lastSyncAt?: string;
  lastSyncError?: string | null;
  /** Non-fatal problems from the last good sync, plain English. Empty or missing = none. */
  lastSyncWarnings?: string[];
}

export interface GoogleAdsStatus extends BaseIntegrationStatus {
  customerId?: string;
}

export interface MetaAdsStatus extends BaseIntegrationStatus {
  adAccountId?: string;
  adAccountName?: string;
  pixelId?: string | null;
}

export interface GbpStatus extends BaseIntegrationStatus {
  displayName?: string;
  locationName?: string;
}

export interface GscStatus extends BaseIntegrationStatus {
  propertyUrl?: string | null;
  totalClicks?: number | null;
  prevTotalClicks?: number | null;
}

export interface GscProperty {
  siteUrl: string;
  permissionLevel: string;
}

export interface GbpListing {
  accountId: string;
  locationName: string;
  displayName: string;
  address: string;
  phone: string;
}

export const integrationsApi = {
  getAll: async (locationId: string): Promise<ApiResponse<Integration[]>> => {
    const response = await apiClient.get<ApiResponse<Integration[]>>('/integrations', {
      params: { locationId },
    });
    return response.data;
  },

  googleAds: {
    getStatus: async (): Promise<ApiResponse<GoogleAdsStatus>> => {
      const response = await apiClient.get<ApiResponse<GoogleAdsStatus>>(
        '/integrations/google-ads/status',
      );
      return response.data;
    },

    initiateConnect: async (): Promise<ApiResponse<{ authUrl: string }>> => {
      const response = await apiClient.get<ApiResponse<{ authUrl: string }>>(
        '/integrations/google-ads/connect',
      );
      return response.data;
    },

    completeSetup: async (
      customerId: string,
      tempToken: string,
      managerCustomerId?: string,
    ): Promise<ApiResponse<{ message: string }>> => {
      const response = await apiClient.post<ApiResponse<{ message: string }>>(
        '/integrations/google-ads/setup',
        { customerId, tempToken, ...(managerCustomerId && { managerCustomerId }) },
      );
      return response.data;
    },

    disconnect: async (): Promise<void> => {
      await apiClient.delete('/integrations/google-ads/disconnect');
    },

    triggerSync: async (): Promise<ApiResponse<{ message: string }>> => {
      const response = await apiClient.post<ApiResponse<{ message: string }>>(
        '/integrations/google-ads/sync',
      );
      return response.data;
    },
  },

  metaAds: {
    /** With `locationId` the status is that location's own; without it, practice-wide. */
    getStatus: async (locationId?: string): Promise<ApiResponse<MetaAdsStatus>> => {
      const response = await apiClient.get<ApiResponse<MetaAdsStatus>>(
        '/integrations/meta-ads/status',
        locationId ? { params: { locationId } } : undefined,
      );
      return response.data;
    },

    updateConfig: async (pixelId: string | null): Promise<ApiResponse<{ message: string }>> => {
      const response = await apiClient.patch<ApiResponse<{ message: string }>>(
        '/integrations/meta-ads/config',
        { pixelId },
      );
      return response.data;
    },

    disconnect: async (): Promise<void> => {
      await apiClient.delete('/integrations/meta-ads/disconnect');
    },

    triggerSync: async (): Promise<ApiResponse<{ message: string }>> => {
      const response = await apiClient.post<ApiResponse<{ message: string }>>(
        '/integrations/meta-ads/sync',
      );
      return response.data;
    },
  },

  gsc: {
    getStatus: async (locationId: string): Promise<ApiResponse<GscStatus>> => {
      const response = await apiClient.get<ApiResponse<GscStatus>>('/integrations/gsc/status', {
        params: { locationId },
      });
      return response.data;
    },

    initiateConnect: async (): Promise<ApiResponse<{ authUrl: string }>> => {
      const response = await apiClient.get<ApiResponse<{ authUrl: string }>>(
        '/integrations/gsc/connect',
      );
      return response.data;
    },

    getProperties: async (
      tempToken: string,
    ): Promise<ApiResponse<{ properties: GscProperty[] }>> => {
      const response = await apiClient.get<ApiResponse<{ properties: GscProperty[] }>>(
        `/integrations/gsc/properties?t=${encodeURIComponent(tempToken)}`,
      );
      return response.data;
    },

    selectProperty: async (
      locationId: string,
      propertyUrl: string,
      tempToken: string,
    ): Promise<ApiResponse<{ message: string }>> => {
      const response = await apiClient.post<ApiResponse<{ message: string }>>(
        '/integrations/gsc/select-property',
        { locationId, propertyUrl, tempToken },
      );
      return response.data;
    },

    disconnect: async (locationId: string): Promise<void> => {
      await apiClient.delete('/integrations/gsc/disconnect', { data: { locationId } });
    },

    triggerSync: async (locationId: string): Promise<ApiResponse<{ message: string }>> => {
      const response = await apiClient.post<ApiResponse<{ message: string }>>(
        '/integrations/gsc/sync',
        { locationId },
      );
      return response.data;
    },
  },

  gbp: {
    getStatus: async (locationId: string): Promise<ApiResponse<GbpStatus>> => {
      const response = await apiClient.get<ApiResponse<GbpStatus>>(
        '/integrations/google-business-profile/status',
        {
          params: { locationId },
        },
      );
      return response.data;
    },

    initiateConnect: async (): Promise<ApiResponse<{ authUrl: string }>> => {
      const response = await apiClient.get<ApiResponse<{ authUrl: string }>>(
        '/integrations/google-business-profile/connect',
      );
      return response.data;
    },

    getListings: async (tempToken: string): Promise<ApiResponse<{ listings: GbpListing[] }>> => {
      const response = await apiClient.get<ApiResponse<{ listings: GbpListing[] }>>(
        `/integrations/google-business-profile/listings?t=${encodeURIComponent(tempToken)}`,
      );
      return response.data;
    },

    completeSetup: async (
      locationId: string,
      accountId: string,
      locationName: string,
      displayName: string,
      tempToken: string,
    ): Promise<ApiResponse<{ message: string }>> => {
      const response = await apiClient.post<ApiResponse<{ message: string }>>(
        '/integrations/google-business-profile/setup',
        { locationId, accountId, locationName, displayName, tempToken },
      );
      return response.data;
    },

    disconnect: async (locationId: string): Promise<void> => {
      await apiClient.delete('/integrations/google-business-profile/disconnect', {
        data: { locationId },
      });
    },

    triggerSync: async (locationId: string): Promise<ApiResponse<{ message: string }>> => {
      const response = await apiClient.post<ApiResponse<{ message: string }>>(
        '/integrations/google-business-profile/sync',
        { locationId },
      );
      return response.data;
    },
  },
};
